import { enhancedApi } from '@store/generated/waiting-room';

const WAITING_ROOM_TAG_TYPES = [
  'WaitingRoomLink',
  'WaitingRoomQueue',
  'WaitingRoomSettings',
] as const;

/**
 * Provider waiting-room settings (GDN-2947).
 *
 * `always_open === true` keeps the waiting room open regardless of the
 * provider's live online/offline presence — patients can check in even
 * while the provider is offline. `false` (default) restores the
 * offline-blocks-check-in behavior.
 */
export type { WaitingRoomSettingsResponse as WaitingRoomSettings } from '@store/generated/waiting-room';

/**
 * Response shape for the GDN-1538 explicit end-session endpoint.
 * Kept in sync with `WaitingRoomEndSessionResponse` (Pydantic).
 *
 * Defined inline rather than imported from generated/ because the
 * endpoint is wired via `injectEndpoints` below so the FE can call it
 * without needing a fresh codegen pass against the new OpenAPI schema.
 */
export interface WaitingRoomEndSessionResponse {
  encounter_id: string;
  room_deleted: boolean;
  queue_entry_ids: string[];
  participants_at_end: number;
  duration_seconds: number | null;
}

export const waitingRoomApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: WAITING_ROOM_TAG_TYPES,
    endpoints: {
      getV2ProviderWaitingRoomLink: {
        providesTags: [{ type: 'WaitingRoomLink', id: 'CURRENT' }],
      },
      getV2ProviderWaitingRoomQueue: {
        providesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      putV2ProviderWaitingRoomAvailability: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      postV2ProviderWaitingRoomQueueByEntryIdAdmit: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      postV2ProviderWaitingRoomEncountersByEncounterIdJoin: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      postV2ProviderWaitingRoomNotifyPatient: {
        invalidatesTags: [{ type: 'WaitingRoomLink', id: 'CURRENT' }],
      },
      deleteV2ProviderWaitingRoomQueueByEntryId: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      // GDN-2947 waiting-room settings (keep waiting room always open).
      getV2ProviderWaitingRoomSettings: {
        providesTags: [{ type: 'WaitingRoomSettings', id: 'CURRENT' }],
      },
      putV2ProviderWaitingRoomSettings: {
        invalidatesTags: [{ type: 'WaitingRoomSettings', id: 'CURRENT' }],
        // Optimistic update — the toggle flips immediately instead of waiting
        // for the PUT + refetch round trips; rolled back if the save fails.
        onQueryStarted: async ({ waitingRoomSettingsRequest }, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            waitingRoomApi.util.updateQueryData(
              'getV2ProviderWaitingRoomSettings',
              undefined,
              draft => {
                draft.always_open = waitingRoomSettingsRequest.always_open;
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // GDN-1538 end-session.
      // Injected (not codegen'd) so the FE can ship without waiting for
      // a fresh OpenAPI pull. The mutation is idempotent and the
      // backend route is `POST /v2/provider/waiting-room/encounters/{id}/end`.
      endWaitingRoomSession: builder.mutation<
        WaitingRoomEndSessionResponse,
        { encounterId: string }
      >({
        query: ({ encounterId }) => ({
          url: `/v2/provider/waiting-room/encounters/${encounterId}/end`,
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetV2ProviderWaitingRoomLinkQuery: useGetProviderWaitingRoomLinkQuery,
  useGetV2ProviderWaitingRoomQueueQuery: useGetProviderWaitingRoomQueueQuery,
  usePutV2ProviderWaitingRoomAvailabilityMutation: useSetProviderWaitingRoomAvailabilityMutation,
  usePostV2ProviderWaitingRoomHeartbeatMutation: useProviderWaitingRoomHeartbeatMutation,
  usePostV2ProviderWaitingRoomQueueByEntryIdAdmitMutation: useAdmitWaitingRoomPatientMutation,
  usePostV2ProviderWaitingRoomEncountersByEncounterIdJoinMutation:
    useJoinWaitingRoomEncounterMutation,
  usePostV2ProviderWaitingRoomNotifyPatientMutation: useNotifyWaitingRoomPatientMutation,
  useDeleteV2ProviderWaitingRoomQueueByEntryIdMutation: useRemoveWaitingRoomPatientMutation,
  useEndWaitingRoomSessionMutation,
  useGetV2ProviderWaitingRoomSettingsQuery: useGetWaitingRoomSettingsQuery,
  usePutV2ProviderWaitingRoomSettingsMutation: useUpdateWaitingRoomSettingsMutation,
} = waitingRoomApi;
